import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import {
  CssBaseline,
  Box,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Typography,
  TextField,
  Button,
  Tooltip,
  Card,
  CardContent,
  Chip,
  useMediaQuery,
  InputAdornment,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import '../App.css';

// Custom dark theme (same as EVTool.js)
const customTheme = createTheme({
  palette: {
    primary: { main: '#ff1744', contrastText: '#ffffff' },
    background: { default: '#121212', paper: '#1e1e1e' },
  },
  typography: { fontFamily: 'Roboto, Arial, sans-serif', h5: { fontWeight: 600 } },
  components: {
    MuiCheckbox: {
      styleOverrides: { root: { color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } } },
    },
    MuiInputLabel: { styleOverrides: { root: { color: '#ffffff' } } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          backgroundColor: '#333333',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
        },
      },
    },
    MuiMenu: { styleOverrides: { paper: { backgroundColor: '#333333', color: '#ffffff' } } },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          '&.Mui-selected': { backgroundColor: '#444444' },
          '&.Mui-selected:hover': { backgroundColor: '#555555' },
          '&:hover': { backgroundColor: '#444444' },
        },
      },
    },
  },
});

// Helper function to convert American odds to decimal
const americanToDecimal = (american) => {
  if (american > 0) {
    return (american / 100) + 1;
  } else {
    return (100 / Math.abs(american)) + 1;
  }
};

// Helper function to calculate profit from stakes and odds
const calculateProfit = (stake1, stake2, price1, price2) => {
  const decimal1 = americanToDecimal(price1);
  const decimal2 = americanToDecimal(price2);
  const payout1 = stake1 * decimal1;
  const payout2 = stake2 * decimal2;
  const totalStake = stake1 + stake2;
  // Both payouts should be approximately equal in a true arbitrage
  return Math.min(payout1, payout2) - totalStake;
};

// Memoized DeeplinkCell component (matches EVTool.js)
const DeeplinkCell = React.memo(({ value }) => {
  const disabled = !value;
  return (
    <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
      <Button
        onClick={() => { if (!disabled) window.open(value, '_blank', 'width=1000,height=800,scrollbars=yes'); }}
        disabled={disabled}
        size="small"
        sx={{
          color: disabled ? 'gray' : '#00ff00',
          backgroundColor: 'transparent',
          border: 'none',
          padding: '4px 8px',
          fontSize: '0.875rem',
          minWidth: 'auto',
          '&.Mui-disabled': {
            color: 'gray',
          },
        }}
      >
        Bet Now
      </Button>
    </Box>
  );
});

// Editable Stake Cell Component
const EditableStakeCell = React.memo(({ value, rowId, field, onStakeChange }) => {
  const [localValue, setLocalValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const handleFocus = (e) => {
    e.target.select();
    setLocalValue('');
  };

  const handleBlur = () => {
    setIsEditing(false);
    const numValue = parseFloat(localValue);
    if (!isNaN(numValue) && numValue >= 0) {
      // Round to nearest cent
      const roundedValue = Math.round(numValue * 100) / 100;
      onStakeChange(rowId, field, roundedValue);
    } else {
      // Revert to original value if empty or invalid
      setLocalValue(value.toFixed(2));
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  if (isEditing) {
    return (
      <TextField
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyPress={handleKeyPress}
        size="small"
        type="number"
        inputProps={{ 
          min: 0, 
          step: 0.01,
          style: { 
            MozAppearance: 'textfield',
            WebkitAppearance: 'none',
            appearance: 'none'
          }
        }}
        autoFocus
        sx={{
          width: '85px',
          '& .MuiInputBase-input': {
            padding: '4px 8px',
            fontSize: '14px',
            color: '#ffffff',
          },
          '& input[type=number]': {
            MozAppearance: 'textfield',
          },
          '& input[type=number]::-webkit-outer-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
          },
          '& input[type=number]::-webkit-inner-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
          },
        }}
      />
    );
  }

  return (
    <Box
      onClick={() => setIsEditing(true)}
      sx={{
        cursor: 'pointer',
        '&:hover': { 
          backgroundColor: 'rgba(255, 255, 255, 0.08)',
        },
        padding: '4px 8px',
        borderRadius: '4px',
        transition: 'all 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '85px',
      }}
    >
      <Typography variant="body2" sx={{ color: '#ffffff' }}>
        ${value.toFixed(2)}
      </Typography>
      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '11px' }}>
        ✎
      </Typography>
    </Box>
  );
});

// Mobile Arbitrage Card Component with editable stakes
const MobileArbCard = React.memo(({ arb, defaultStake, onStakeChange }) => {
  // Parse event info
  const [awayTeam, homeTeam] = (arb.eventShortName || '').split('@');
  const eventDate = arb.eventStart
    ? new Date(arb.eventStart).toLocaleString('en-US', {
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }).replace(',', '')
    : '';
  
  // Use stakes from arb object (which may have been manually adjusted)
  const stake1 = arb.stake1;
  const stake2 = arb.stake2;
  const guaranteedProfit = arb.actualProfit;
  const totalStake = stake1 + stake2;
  const roi = totalStake > 0 ? (guaranteedProfit / totalStake) * 100 : 0;
  
  // Conditional formatting for ROI
  let roiColor = '#ffffff';
  if (roi >= 10) {
    roiColor = '#00ff00'; // Bright green for 10%+ ROI
  } else if (roi >= 5) {
    roiColor = '#66ff66'; // Medium green for 5-10% ROI
  } else if (roi >= 2) {
    roiColor = '#99ff99'; // Light green for 2-5% ROI
  } else if (roi >= 1) {
    roiColor = '#ccffcc'; // Very light green for 1-2% ROI
  }
  
  // Format lines
  const formatLine = (side, points) => {
    const sideAbbr = side === 'Over' ? 'o' : side === 'Under' ? 'u' : '';
    return `${sideAbbr}${points || ''}`.trim();
  };
  
  const line1 = formatLine(arb.side1, arb.points1);
  const line2 = formatLine(arb.side2, arb.points1);

  const [editingStake1, setEditingStake1] = useState(false);
  const [editingStake2, setEditingStake2] = useState(false);
  const [tempStake1, setTempStake1] = useState('');
  const [tempStake2, setTempStake2] = useState('');

  const handleStake1Focus = (e) => {
    e.target.select();
    setTempStake1('');
  };

  const handleStake2Focus = (e) => {
    e.target.select();
    setTempStake2('');
  };

  const handleStake1Change = () => {
    setEditingStake1(false);
    const numValue = parseFloat(tempStake1);
    if (!isNaN(numValue) && numValue >= 0) {
      // Round to nearest cent
      const roundedValue = Math.round(numValue * 100) / 100;
      onStakeChange(arb.id, 'stake1', roundedValue);
    }
    // Reset temp value for next edit
    setTempStake1('');
  };

  const handleStake2Change = () => {
    setEditingStake2(false);
    const numValue = parseFloat(tempStake2);
    if (!isNaN(numValue) && numValue >= 0) {
      // Round to nearest cent
      const roundedValue = Math.round(numValue * 100) / 100;
      onStakeChange(arb.id, 'stake2', roundedValue);
    }
    // Reset temp value for next edit
    setTempStake2('');
  };
  
  return (
    <Card sx={{ 
      mb: 2, 
      border: '1px solid #333',
      backgroundColor: '#1e1e1e',
      borderRadius: 2,
      overflow: 'hidden'
    }}>
      {/* Profit Header - Always visible */}
      <Box sx={{ 
        backgroundColor: '#2a2a2a',
        px: 2,
        py: 1.5,
        borderBottom: '1px solid #333'
      }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ color: '#ffffff' }}>
            ${guaranteedProfit.toFixed(2)} Profit
          </Typography>
          <Chip 
            label={`${roi.toFixed(1)}% ROI`}
            size="small"
            sx={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: roiColor,
              fontWeight: 500
            }}
          />
        </Stack>
      </Box>
      
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* Player and Bet Type */}
        {(arb.playerName || arb.betTypeName) && (
          <Box mb={2}>
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#ffffff' }}>
              {arb.playerName}
            </Typography>
            <Typography variant="body2" sx={{ color: '#BBBBBB' }}>
              {arb.betTypeName}
            </Typography>
          </Box>
        )}
        
        {/* Event Info */}
        <Box mb={2}>
          <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
            {arb.name && (
              <>
                <Typography variant="body2" sx={{ color: '#ffffff' }}>
                  {arb.name}
                </Typography>
                <Typography variant="body2" sx={{ color: '#ffffff' }}>
                  |
                </Typography>
              </>
            )}
            {arb.awayLogo && (
              <img
                src={arb.awayLogo}
                alt={awayTeam}
                style={{ width: 'auto', height: '16px', objectFit: 'contain' }}
                loading="lazy"
              />
            )}
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#ffffff' }}>
              {awayTeam}
            </Typography>
            <Typography variant="body2" sx={{ mx: 0.5, color: '#ffffff' }}>
              @
            </Typography>
            {arb.homeLogo && (
              <img
                src={arb.homeLogo}
                alt={homeTeam}
                style={{ width: 'auto', height: '16px', objectFit: 'contain' }}
                loading="lazy"
              />
            )}
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#ffffff' }}>
              {homeTeam}
            </Typography>
          </Stack>
          <Typography variant="caption" sx={{ color: '#BBBBBB' }}>
            {eventDate}
          </Typography>
        </Box>
        
        {/* Two Bets Side by Side */}
        <Stack spacing={2}>
          {/* Bet 1 */}
          <Box sx={{ 
            p: 1.5,
            borderRadius: 1,
            backgroundColor: '#2a2a2a',
            border: '1px solid #444'
          }}>
            {/* Top row: Logo and Button */}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
              <img
                src={arb.logo1}
                alt={arb.ms1}
                style={{ width: 60, height: 30, objectFit: 'contain' }}
                loading="lazy"
              />
              <Button
                variant="contained"
                size="small"
                onClick={() => arb.deeplink1 && window.open(arb.deeplink1, '_blank')}
                disabled={!arb.deeplink1}
                sx={{
                  backgroundColor: '#00ff00',
                  color: '#000',
                  minWidth: 70,
                  '&:hover': { backgroundColor: '#00cc00' },
                  '&.Mui-disabled': { backgroundColor: '#333', color: '#666' }
                }}
              >
                Bet
              </Button>
            </Stack>
            
            {/* Line/Odds */}
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#ffffff', mb: 0.5 }}>
              {line1} {arb.price1 < 0 ? arb.price1 : `+${arb.price1}`}
            </Typography>
            
            {/* Stake */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" sx={{ color: '#ffffff' }}>
                Stake: $
              </Typography>
              {editingStake1 ? (
                <TextField
                  value={tempStake1}
                  onChange={(e) => setTempStake1(e.target.value)}
                  onBlur={handleStake1Change}
                  onKeyPress={(e) => e.key === 'Enter' && handleStake1Change()}
                  size="small"
                  type="number"
                  inputProps={{ 
                    min: 0, 
                    step: 0.01,
                    style: { 
                      MozAppearance: 'textfield',
                      WebkitAppearance: 'none',
                      appearance: 'none'
                    }
                  }}
                  autoFocus
                  sx={{
                    width: '70px',
                    '& .MuiInputBase-input': {
                      padding: '2px 6px',
                      fontSize: '12px',
                      color: '#ffffff',
                    },
                    '& input[type=number]': {
                      MozAppearance: 'textfield',
                    },
                    '& input[type=number]::-webkit-outer-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                    '& input[type=number]::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                  }}
                />
              ) : (
                <Box
                  onClick={() => setEditingStake1(true)}
                  sx={{ 
                    color: '#ffffff',
                    cursor: 'pointer',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '2px',
                    '&:hover': { 
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    }
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#ffffff' }}>
                    {stake1.toFixed(2)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '10px' }}>
                    ✎
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
          
          {/* Bet 2 */}
          <Box sx={{ 
            p: 1.5,
            borderRadius: 1,
            backgroundColor: '#2a2a2a',
            border: '1px solid #444'
          }}>
            {/* Top row: Logo and Button */}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
              <img
                src={arb.logo2}
                alt={arb.ms2}
                style={{ width: 60, height: 30, objectFit: 'contain' }}
                loading="lazy"
              />
              <Button
                variant="contained"
                size="small"
                onClick={() => arb.deeplink2 && window.open(arb.deeplink2, '_blank')}
                disabled={!arb.deeplink2}
                sx={{
                  backgroundColor: '#00ff00',
                  color: '#000',
                  minWidth: 70,
                  '&:hover': { backgroundColor: '#00cc00' },
                  '&.Mui-disabled': { backgroundColor: '#333', color: '#666' }
                }}
              >
                Bet
              </Button>
            </Stack>
            
            {/* Line/Odds */}
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#ffffff', mb: 0.5 }}>
              {line2} {arb.price2 < 0 ? arb.price2 : `+${arb.price2}`}
            </Typography>
            
            {/* Stake */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" sx={{ color: '#ffffff' }}>
                Stake: $
              </Typography>
              {editingStake2 ? (
                <TextField
                  value={tempStake2}
                  onChange={(e) => setTempStake2(e.target.value)}
                  onBlur={handleStake2Change}
                  onKeyPress={(e) => e.key === 'Enter' && handleStake2Change()}
                  size="small"
                  type="number"
                  inputProps={{ 
                    min: 0, 
                    step: 0.01,
                    style: { 
                      MozAppearance: 'textfield',
                      WebkitAppearance: 'none',
                      appearance: 'none'
                    }
                  }}
                  autoFocus
                  sx={{
                    width: '70px',
                    '& .MuiInputBase-input': {
                      padding: '2px 6px',
                      fontSize: '12px',
                      color: '#ffffff',
                    },
                    '& input[type=number]': {
                      MozAppearance: 'textfield',
                    },
                    '& input[type=number]::-webkit-outer-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                    '& input[type=number]::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                  }}
                />
              ) : (
                <Box
                  onClick={() => setEditingStake2(true)}
                  sx={{ 
                    color: '#ffffff',
                    cursor: 'pointer',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '2px',
                    '&:hover': { 
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    }
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#ffffff' }}>
                    {stake2.toFixed(2)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '10px' }}>
                    ✎
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
});

const ArbDataGrid = ({ arbitrages, updatesPaused }) => {
  // State for filters and total stake
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  
  // Load saved filters and stake from localStorage
  const savedLeagueFilter = useMemo(() => {
    try {
      const saved = localStorage.getItem('arbLeagueFilter');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  }, []);

  const savedBookFilter = useMemo(() => {
    try {
      const saved = localStorage.getItem('arbBookFilter');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  }, []);

  const savedDefaultStake = useMemo(() => {
    try {
      const saved = localStorage.getItem('arbDefaultStake');
      return saved ? parseFloat(saved) : 1000;
    } catch {
      return 1000;
    }
  }, []);

  const [leagueFilter, setLeagueFilter] = useState(savedLeagueFilter);
  const [bookFilter, setBookFilter] = useState(savedBookFilter);
  const leagueInitialized = useRef(false);
  const bookInitialized = useRef(false);
  const [defaultStake, setDefaultStake] = useState(savedDefaultStake);
  const [currentPage, setCurrentPage] = useState(0);
  const [manualStakes, setManualStakes] = useState({});

  // Save filters and stake to localStorage
  useEffect(() => {
    localStorage.setItem('arbLeagueFilter', JSON.stringify(leagueFilter));
  }, [leagueFilter]);

  useEffect(() => {
    localStorage.setItem('arbBookFilter', JSON.stringify(bookFilter));
  }, [bookFilter]);

  useEffect(() => {
    localStorage.setItem('arbDefaultStake', defaultStake.toString());
  }, [defaultStake]);

  // Check if mobile
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Handle screen resize
  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle stake changes
  const handleStakeChange = (rowId, field, value) => {
    const arb = filteredArbitrages.find(a => a.id === rowId);
    if (!arb) return;

    const decimal1 = americanToDecimal(arb.price1);
    const decimal2 = americanToDecimal(arb.price2);

    let newStake1, newStake2;

    if (field === 'stake1') {
      newStake1 = value;
      // Calculate stake2 to maintain arbitrage ratio
      newStake2 = value * (decimal1 / decimal2);
    } else {
      newStake2 = value;
      // Calculate stake1 to maintain arbitrage ratio
      newStake1 = value * (decimal2 / decimal1);
    }

    // Round both stakes to nearest cent
    newStake1 = Math.round(newStake1 * 100) / 100;
    newStake2 = Math.round(newStake2 * 100) / 100;

    setManualStakes(prev => ({
      ...prev,
      [rowId]: {
        stake1: newStake1,
        stake2: newStake2,
      }
    }));
  };

  // Reset stakes for a specific arbitrage
  const resetStakes = (rowId) => {
    setManualStakes(prev => {
      const newStakes = { ...prev };
      delete newStakes[rowId];
      return newStakes;
    });
  };

  // Compute filter options and filtered/sorted arbitrages
  const { filterOptions, filteredArbitrages } = useMemo(() => {
    if (!arbitrages || !arbitrages.length) {
      return {
        filterOptions: { leagues: [], books: [] },
        filteredArbitrages: [],
      };
    }

    // Build filter options
    const leagues = new Set();
    const books = new Set();
    arbitrages.forEach((arb) => {
      if (arb.name) leagues.add(arb.name);
      if (arb.ms1) books.add(arb.ms1);
      if (arb.ms2) books.add(arb.ms2);
    });

    // Apply filters
    const leagueSet = new Set(leagueFilter);
    const bookSet = new Set(bookFilter);
    const filtered = arbitrages.filter((arb) => {
      if (leagueFilter.length > 0 && arb.name && !leagueSet.has(arb.name)) {
        return false;
      }
      if (bookFilter.length > 0) {
        const matchesBook1 = arb.ms1 && bookSet.has(arb.ms1);
        const matchesBook2 = arb.ms2 && bookSet.has(arb.ms2);
        if (!matchesBook1 || !matchesBook2) {
          return false;
        }
      }
      return true;
    });

    // Adjust stakes and guaranteedProfit based on defaultStake or manual overrides
    const baseTotalStake = 100;
    const scaleFactor = defaultStake / baseTotalStake;
    const adjusted = filtered.map(arb => {
      let stake1, stake2;
      
      // Check if there are manual stakes for this arbitrage
      if (manualStakes[arb.id]) {
        stake1 = manualStakes[arb.id].stake1;
        stake2 = manualStakes[arb.id].stake2;
      } else {
        // Use default calculation
        stake1 = Math.round(arb.stake1 * scaleFactor * 100) / 100;
        stake2 = Math.round(arb.stake2 * scaleFactor * 100) / 100;
      }

      const actualProfit = calculateProfit(stake1, stake2, arb.price1, arb.price2);

      return {
        ...arb,
        stake1,
        stake2,
        actualProfit,
        hasManualStakes: !!manualStakes[arb.id],
      };
    });

    // Sort by ROI (actualProfit / totalStake) descending
    const sorted = [...adjusted].sort((a, b) => {
      const roiA = (a.stake1 + a.stake2) > 0 ? (a.actualProfit / (a.stake1 + a.stake2)) : 0;
      const roiB = (b.stake1 + b.stake2) > 0 ? (b.actualProfit / (b.stake1 + b.stake2)) : 0;
      return roiB - roiA;
    });

    return {
      filterOptions: {
        leagues: Array.from(leagues),
        books: Array.from(books),
      },
      filteredArbitrages: sorted,
    };
  }, [arbitrages, leagueFilter, bookFilter, defaultStake, manualStakes]);

  // Initialize filters only once when options are available
  useEffect(() => {
    if (filterOptions.leagues.length > 0 && !leagueInitialized.current) {
      // If we have saved filters, validate them against current options
      if (savedLeagueFilter.length > 0) {
        const validSavedFilters = savedLeagueFilter.filter(league => 
          filterOptions.leagues.includes(league)
        );
        if (validSavedFilters.length > 0) {
          setLeagueFilter(validSavedFilters);
        } else {
          setLeagueFilter(filterOptions.leagues);
        }
      } else {
        setLeagueFilter(filterOptions.leagues);
      }
      leagueInitialized.current = true;
    }
  }, [filterOptions.leagues, savedLeagueFilter]);

  useEffect(() => {
    if (filterOptions.books.length > 0 && !bookInitialized.current) {
      // If we have saved filters, validate them against current options
      if (savedBookFilter.length > 0) {
        const validSavedFilters = savedBookFilter.filter(book => 
          filterOptions.books.includes(book)
        );
        // Ensure at least 2 books are selected
        if (validSavedFilters.length >= 2) {
          setBookFilter(validSavedFilters);
        } else if (validSavedFilters.length === 1) {
          // Add one more book if only one valid saved filter
          const additionalBook = filterOptions.books.find(book => 
            !validSavedFilters.includes(book)
          );
          setBookFilter([...validSavedFilters, additionalBook]);
        } else {
          // Default to first 2 books if no valid saved filters
          setBookFilter(filterOptions.books.slice(0, 2));
        }
      } else {
        setBookFilter(filterOptions.books);
      }
      bookInitialized.current = true;
    }
  }, [filterOptions.books, savedBookFilter]);

  // Filter handlers with minimum book selection enforcement
  const handleFilterChange = (e, options, setter, type) => {
    const val = e.target.value;
    if (type === 'book' && val.length < 2) {
      if (val.length === 0) {
        setter(options.slice(0, 2));
      } else if (val.length === 1) {
        const remainingOptions = options.filter(opt => !val.includes(opt));
        setter([...val, remainingOptions[0]]);
      }
      return;
    }

    if (val[val.length - 1] === 'all') {
      setter(prev => {
        const newValue = prev.length === options.length ? [] : options;
        if (type === 'book' && newValue.length < 2) {
          const enforcedValue = options.slice(0, 2);
          return enforcedValue;
        }
        return newValue;
      });
    } else {
      setter(val);
    }
  };

  const renderFilterValue = (sel, opts, label) => {
    if (!sel.length) return label;
    if (sel.length === opts.length) return 'All';
    return sel.length > 1 ? `Multiple selections (${sel.length})` : sel[0];
  };

  // Handle default stake input change
  const handleDefaultStakeChange = (event) => {
    const value = event.target.value;
    if (value === '') {
      setDefaultStake('');
    } else {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        setDefaultStake(Math.max(1, numValue));
      }
    }
  };

  // Handle blur to ensure minimum stake
  const handleDefaultStakeBlur = () => {
    if (defaultStake === '' || defaultStake < 1) {
      setDefaultStake(1);
    }
  };

  // Mobile pagination
  const mobilePageSize = 5;
  const totalPages = Math.ceil(filteredArbitrages.length / mobilePageSize);
  const startIdx = currentPage * mobilePageSize;
  const endIdx = startIdx + mobilePageSize;
  const paginatedArbitrages = filteredArbitrages.slice(startIdx, endIdx);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [leagueFilter, bookFilter]);

  // Define columns with stacked Book 1 and Book 2 data, ensuring consistent alignment
  const columns = [
    {
      field: 'eventShortName',
      headerName: 'Event',
      minWidth: 165,
      flex: 2,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) => {
        const eventStart = params.row.eventStart
          ? new Date(params.row.eventStart).toLocaleString('en-US', {
              year: '2-digit',
              month: 'numeric',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            }).replace(',', '')
          : '';
        const [awayTeam, homeTeam] = params.value.split('@');
        const leagueName = params.row.name || '';
        
        return (
          <Box sx={{ whiteSpace: 'pre-line', textAlign: 'left', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', paddingLeft: '8px', flexWrap: 'wrap' }}>
              {/* League name */}
              {leagueName && (
                <>
                  <Typography sx={{ fontSize: '14px', color: '#ffffff', mr: 0 }}>
                    {leagueName}
                  </Typography>
                  <Typography sx={{ fontSize: '14px', color: '#ffffff', mx: 0.5 }}>
                   |
                  </Typography>
                </>
              )}
              {/* Away team */}
              {params.row.awayLogo && (
                <img
                  src={params.row.awayLogo}
                  alt={`${awayTeam} Logo`}
                  style={{ width: 'auto', height: '14px', paddingBottom: '0px', paddingRight: '2px', objectFit: 'contain' }}
                  loading="lazy"
                />
              )}
              <Typography sx={{ fontSize: '14px' }}>{awayTeam}</Typography>
              <Typography sx={{ fontSize: '12px', mx: '2px', paddingBottom: '2px' }}>@</Typography>
              {/* Home team */}
              {params.row.homeLogo && (
                <img
                  src={params.row.homeLogo}
                  alt={`${homeTeam} Logo`}
                  style={{ width: 'auto', height: '14px', paddingBottom: '0px', paddingRight: '2px', objectFit: 'contain' }}
                  loading="lazy"
                />
              )}
              <Typography sx={{ fontSize: '14px' }}>{homeTeam}</Typography>
            </Box>
            <Typography sx={{ fontSize: '14px', paddingLeft: '8px', color: '#BBBBBB' }}>{eventStart}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'playerName',
      headerName: 'Bet Info',
      minWidth: 165,
      flex: 2,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) => {
        const playerName = params.row.playerName || '';
        const betTypeName = params.row.betTypeName || '';
        return (
          <Box sx={{ textAlign: 'left', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', width: '100%' }}>
            <Tooltip title={playerName} placement="top" arrow>
              <Typography
                sx={{
                  fontSize: '14px',
                  paddingLeft: '8px',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  width: '100%',
                }}
              >
                {playerName}
              </Typography>
            </Tooltip>
            <Tooltip title={betTypeName} placement="top" arrow>
              <Typography
                sx={{
                  fontSize: '14px',
                  paddingLeft: '8px',
                  color: '#BBBBBB',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  width: '100%',
                }}
              >
                {betTypeName}
              </Typography>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: 'logo1',
      headerName: 'Book',
      minWidth: 90,
      flex: 0.1,
      sortable: false,
      headerAlign: 'left',
      align: 'left',
      renderCell: params => (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              width: '100%',
              height: '50%',
              paddingTop: '2px',
            }}
          >
            <img
              src={params.value}
              alt="Logo 1"
              style={{ width: 72, height: 36, objectFit: 'contain' }}
              loading="lazy"
            />
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              width: '100%',
              height: '50%',
              paddingBottom: '2px',
            }}
          >
            <img
              src={params.row.logo2}
              alt="Logo 2"
              style={{ width: 72, height: 36, objectFit: 'contain' }}
              loading="lazy"
            />
          </div>
        </Box>
      ),
    },
    {
      field: 'lineOdds',
      headerName: 'Line/Odds',
      minWidth: 130,
      flex: 1,
      sortable: false,
      headerAlign: 'right',
      align: 'right',
      renderCell: params => (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            justifyContent: 'center',
            paddingRight: '8px',
          }}
        >
          {/* Book 1 */}
          <Box sx={{ height: '50%', display: 'flex', alignItems: 'center', paddingTop: '2px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              <Box sx={{ minWidth: '60px', textAlign: 'right', mr: 0.5 }}>
                <Typography variant="body2" sx={{ color: '#ffffff' }}>
                  {`${params.row.side1 === 'Over' ? 'o' : params.row.side1 === 'Under' ? 'u' : ''} ${params.row.points1 || ''}`.trim()}
                </Typography>
              </Box>
              <Box sx={{ minWidth: '40px', textAlign: 'right' }}>
                <Typography variant="body2" sx={{ color: '#ffffff' }}>
                  {params.row.price1 < 0 ? params.row.price1 : `+${params.row.price1}`}
                </Typography>
              </Box>
            </Box>
          </Box>
          {/* Book 2 */}
          <Box sx={{ height: '50%', display: 'flex', alignItems: 'center', paddingBottom: '2px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              <Box sx={{ minWidth: '60px', textAlign: 'right', mr: 0.5 }}>
                <Typography variant="body2" sx={{ color: '#ffffff' }}>
                  {`${params.row.side2 === 'Over' ? 'o' : params.row.side2 === 'Under' ? 'u' : ''} ${params.row.points1 || ''}`.trim()}
                </Typography>
              </Box>
              <Box sx={{ minWidth: '40px', textAlign: 'right' }}>
                <Typography variant="body2" sx={{ color: '#ffffff' }}>
                  {params.row.price2 < 0 ? params.row.price2 : `+${params.row.price2}`}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      ),
    },
    {
      field: 'stake1',
      headerName: 'Stake',
      minWidth: 90,
      flex: 1,
      sortable: false,
      headerAlign: 'left',
      align: 'left',
      renderCell: params => (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          {params.row.hasManualStakes && (
            <Tooltip title="Reset to default stakes">
              <Button
                size="small"
                onClick={() => resetStakes(params.row.id)}
                sx={{
                  position: 'absolute',
                  top: '2px',
                  right: '10px',
                  minWidth: '20px',
                  padding: '2px',
                  fontSize: '10px',
                  color: '#ff1744',
                  '&:hover': { backgroundColor: 'rgba(255, 23, 68, 0.08)' }
                }}
              >
                ↺
              </Button>
            </Tooltip>
          )}
          <Box sx={{ height: '50%', display: 'flex', alignItems: 'center', paddingTop: '2px', paddingLeft: '8px' }}>
            <EditableStakeCell
              value={params.value}
              rowId={params.row.id}
              field="stake1"
              onStakeChange={handleStakeChange}
            />
          </Box>
          <Box sx={{ height: '50%', display: 'flex', alignItems: 'center', paddingBottom: '2px', paddingLeft: '8px' }}>
            <EditableStakeCell
              value={params.row.stake2}
              rowId={params.row.id}
              field="stake2"
              onStakeChange={handleStakeChange}
            />
          </Box>
        </Box>
      ),
    },
    {
      field: 'actualProfit',
      headerName: 'Profit',
      minWidth: 125,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      sortComparator: (v1, v2, param1, param2) => {
        // Sort by ROI instead of profit amount
        const totalStake1 = param1.row.stake1 + param1.row.stake2;
        const totalStake2 = param2.row.stake1 + param2.row.stake2;
        const roi1 = totalStake1 > 0 ? (v1 / totalStake1) : 0;
        const roi2 = totalStake2 > 0 ? (v2 / totalStake2) : 0;
        return roi1 - roi2;
      },
      renderCell: (params) => {
        const totalStake = params.row.stake1 + params.row.stake2;
        const roi = totalStake > 0 ? (params.value / totalStake) * 100 : 0;
        
        // Conditional formatting for ROI
        let roiColor = '#ffffff';
        if (roi >= 10) {
          roiColor = '#00ff00'; // Bright green for 10%+ ROI
        } else if (roi >= 5) {
          roiColor = '#66ff66'; // Medium green for 5-10% ROI
        } else if (roi >= 2) {
          roiColor = '#99ff99'; // Light green for 2-5% ROI
        } else if (roi >= 1) {
          roiColor = '#ccffcc'; // Very light green for 1-2% ROI
        }
        
        return (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center',
            height: '100%',
            paddingLeft: '8px'
          }}>
            <Typography variant="body2" sx={{ color: '#ffffff', lineHeight: 1.5 }}>
              ${params.value.toFixed(2)}
            </Typography>
            <Typography variant="body2" sx={{ color: roiColor, fontSize: '0.875rem', lineHeight: 1.5 }}>
              {roi.toFixed(1)}% ROI
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'deeplink1',
      headerName: '',
      sortable: false,
      minWidth: 80,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      renderCell: params => (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ height: '50%', display: 'flex', alignItems: 'center', paddingTop: '2px', paddingLeft: '8px' }}>
            <DeeplinkCell value={params.value} />
          </Box>
          <Box sx={{ height: '50%', display: 'flex', alignItems: 'center', paddingBottom: '2px', paddingLeft: '8px' }}>
            <DeeplinkCell value={params.row.deeplink2} />
          </Box>
        </Box>
      ),
    },
  ];

  // Remove eventStart from columnVisibilityModel since it's now combined
  const columnVisibilityModel = {};

  return (
    <ThemeProvider theme={customTheme}>
      <StyledEngineProvider injectFirst>
        <CssBaseline />
        <Box sx={{ width: '100%', height: '100vh', backgroundColor: 'background.default', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          
          {/* Mobile View */}
          {isMobile ? (
            <>
              {/* Mobile Header */}
              <Box sx={{ 
                position: 'sticky',
                top: 0,
                backgroundColor: '#121212',
                px: 2,
                py: 1.5,
                borderBottom: '1px solid #333',
                zIndex: 10
              }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <FormControl variant="outlined" size="small" sx={{ flex: 1, minWidth: 100 }}>
                      <InputLabel id="mobile-league-filter-label" sx={{ color: '#ffffff' }}>League</InputLabel>
                      <Select
                        multiple
                        labelId="mobile-league-filter-label"
                        label="League"
                        notched
                        value={leagueFilter}
                        onChange={e => handleFilterChange(e, filterOptions.leagues, setLeagueFilter, 'league')}
                        renderValue={sel => renderFilterValue(sel, filterOptions.leagues, 'League')}
                        sx={{ fontSize: '0.875rem' }}
                      >
                        <MenuItem value="all">
                          <Checkbox checked={leagueFilter.length === filterOptions.leagues.length} />
                          <ListItemText primary="Select All" />
                        </MenuItem>
                        {filterOptions.leagues.map(o => (
                          <MenuItem key={o} value={o}>
                            <Checkbox checked={leagueFilter.includes(o)} />
                            <ListItemText primary={o} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl variant="outlined" size="small" sx={{ flex: 1, minWidth: 100 }}>
                      <InputLabel id="mobile-book-filter-label" sx={{ color: '#ffffff' }}>Book</InputLabel>
                      <Select
                        multiple
                        labelId="mobile-book-filter-label"
                        label="Book"
                        notched
                        value={bookFilter}
                        onChange={e => handleFilterChange(e, filterOptions.books, setBookFilter, 'book')}
                        renderValue={sel => renderFilterValue(sel, filterOptions.books, 'Book')}
                        sx={{ fontSize: '0.875rem' }}
                      >
                        <MenuItem value="all">
                          <Checkbox checked={bookFilter.length === filterOptions.books.length} />
                          <ListItemText primary="Select All" />
                        </MenuItem>
                        {filterOptions.books.map(o => (
                          <MenuItem key={o} value={o}>
                            <Checkbox checked={bookFilter.includes(o)} />
                            <ListItemText primary={o} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                  <TextField
                    label="Default Stake"
                    type="number"
                    size="small"
                    value={defaultStake}
                    onChange={handleDefaultStakeChange}
                    onBlur={handleDefaultStakeBlur}
                    InputProps={{
                      startAdornment: <Typography sx={{ color: '#ffffff', mr: 1 }}>$</Typography>,
                      inputProps: { min: 1, step: 1 },
                    }}
                    sx={{ width: '100%' }}
                  />
                </Stack>
              </Box>

              {/* Mobile Content */}
              <Box sx={{ 
                flex: 1,
                overflowY: 'auto',
                p: 2,
                paddingBottom: totalPages > 1 ? '80px' : '16px'
              }}>
                {filteredArbitrages.length === 0 ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px' }}>
                    <Typography sx={{ color: '#ffffff', fontSize: '1rem' }}>
                      No arbitrages available, please check back later
                    </Typography>
                  </Box>
                ) : (
                  paginatedArbitrages.map(arb => (
                    <MobileArbCard 
                      key={arb.id} 
                      arb={arb} 
                      defaultStake={defaultStake}
                      onStakeChange={handleStakeChange}
                    />
                  ))
                )}
              </Box>

              {/* Mobile Pagination */}
              {totalPages > 1 && (
                <Box sx={{ 
                  position: 'fixed',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  backgroundColor: '#1e1e1e',
                  borderTop: '1px solid #333',
                  p: 2,
                  zIndex: 9
                }}>
                  <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
                    <Button
                      size="small"
                      onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                      disabled={currentPage === 0}
                      sx={{ color: '#ffffff' }}
                    >
                      Previous
                    </Button>
                    <Typography variant="body2" sx={{ color: '#ffffff' }}>
                      {currentPage + 1} / {totalPages}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                      disabled={currentPage >= totalPages - 1}
                      sx={{ color: '#ffffff' }}
                    >
                      Next
                    </Button>
                  </Stack>
                </Box>
              )}
            </>
          ) : (
            // Desktop View
            <>
              <Box className="filter-section">
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                  <Stack direction="row" spacing={2}>
                    <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                      <InputLabel id="league-filter-label" sx={{ color: '#ffffff' }}>League</InputLabel>
                      <Select
                        multiple
                        labelId="league-filter-label"
                        label="League"
                        notched
                        value={leagueFilter}
                        onChange={e => handleFilterChange(e, filterOptions.leagues, setLeagueFilter, 'league')}
                        renderValue={sel => renderFilterValue(sel, filterOptions.leagues, 'League')}
                      >
                        <MenuItem value="all">
                          <Checkbox checked={leagueFilter.length === filterOptions.leagues.length} />
                          <ListItemText primary="Select All" />
                        </MenuItem>
                        {filterOptions.leagues.map(o => (
                          <MenuItem key={o} value={o}>
                            <Checkbox checked={leagueFilter.includes(o)} />
                            <ListItemText primary={o} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                      <InputLabel id="book-filter-label" sx={{ color: '#ffffff' }}>Book</InputLabel>
                      <Select
                        multiple
                        labelId="book-filter-label"
                        label="Book"
                        notched
                        value={bookFilter}
                        onChange={e => handleFilterChange(e, filterOptions.books, setBookFilter, 'book')}
                        renderValue={sel => renderFilterValue(sel, filterOptions.books, 'Book')}
                      >
                        <MenuItem value="all">
                          <Checkbox checked={bookFilter.length === filterOptions.books.length} />
                          <ListItemText primary="Select All" />
                        </MenuItem>
                        {filterOptions.books.map(o => (
                          <MenuItem key={o} value={o}>
                            <Checkbox checked={bookFilter.includes(o)} />
                            <ListItemText primary={o} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      label="Default Stake"
                      type="number"
                      size="small"
                      value={defaultStake}
                      onChange={handleDefaultStakeChange}
                      onBlur={handleDefaultStakeBlur}
                      InputProps={{
                        startAdornment: <Typography sx={{ color: '#ffffff', mr: 1 }}>$</Typography>,
                        inputProps: { min: 1, step: 1 },
                      }}
                      sx={{ width: 150 }}
                    />
                  </Stack>
                </Stack>
              </Box>
              <Box className="table-section">
                <Box className="data-grid-container" sx={{ height: '100%', boxSizing: 'border-box' }}>
                  {filteredArbitrages.length === 0 ? (
                    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography sx={{ color: '#ffffff', fontSize: '1rem' }}>
                        No arbitrages available, please check back later
                      </Typography>
                    </Box>
                  ) : (
                    <DataGridPro
                      rows={filteredArbitrages}
                      columns={columns}
                      getRowId={row => row.id}
                      sortingMode="client"
                      disableSelectionOnClick
                      hideFooter
                      columnVisibilityModel={columnVisibilityModel}
                      sx={{
                        '& .MuiDataGrid-columnHeaders': {
                          backgroundColor: '#1e1e1e',
                          borderBottom: '2px solid #444',
                          position: 'sticky',
                          top: '56px',
                          zIndex: 2,
                        },
                        '& .MuiDataGrid-columnHeader:focus': { outline: 'none' },
                        '& .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
                        '& .MuiDataGrid-cell': {
                          color: '#ffffff !important',
                          userSelect: 'none',
                          borderRight: '1px solid rgba(255, 255, 255, 0.3)', // Column separator - increased opacity to 30%
                        },
                        '& .MuiDataGrid-cell:last-child': {
                          borderRight: 'none', // Remove border from last column
                        },
                        '& .MuiDataGrid-cell:focus': {
                          outline: 'none',
                        },
                        '& .MuiDataGrid-cell:focus-within': {
                          outline: 'none',
                        },
                        '& .MuiDataGrid-row:hover': { backgroundColor: '#1a1a1a' },
                        '& .MuiDataGrid-row.Mui-selected': {
                          backgroundColor: '#1a1a1a',
                          '&:hover': { backgroundColor: '#1a1a1a' },
                        },
                        '& .MuiDataGrid-columnSeparator': { 
                          visibility: 'visible',
                          color: 'rgba(255, 255, 255, 0.3)',
                        },
                        '& .MuiDataGrid-sortIcon': { color: '#ffffff !important' },
                        '& .MuiDataGrid-columnHeaderTitle': {
                          color: '#ffffff !important',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          lineHeight: 1.2,
                        },
                        '& .MuiDataGrid-root': {
                          overflowY: 'auto !important',
                          margin: 0,
                          boxSizing: 'border-box',
                        },
                        '& .MuiDataGrid-virtualScroller': {
                          overflowY: 'auto !important',
                        },
                        '& .MuiDataGrid-main': {
                          padding: 0,
                          boxSizing: 'border-box',
                        },
                        '& .MuiDataGrid-footerContainer': {
                          backgroundColor: '#1e1e1e',
                          color: '#ffffff !important',
                          position: 'sticky',
                          bottom: 0,
                          zIndex: 1,
                          boxSizing: 'border-box',
                        },
                      }}
                      rowBuffer={5}
                      rowThreshold={2}
                    />
                  )}
                </Box>
              </Box>
            </>
          )}
        </Box>
      </StyledEngineProvider>
    </ThemeProvider>
  );
};

export default ArbDataGrid;